import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Container,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  InputAdornment,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Instagram as InstagramIcon,
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Podcasts as PodcastsIcon,
  MergeType as MergeIcon,
  Person as PersonIcon,
  LiveTv as LiveTvIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alpha } from '@mui/material/styles';
import apiService, { PublicSocialAccount } from '../services/api';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { useSocialAdminStore } from '../stores/socialAdminStore';

// TikTok icon component
const TikTokIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Format numbers with K/M suffixes
const formatCompactNumber = (num: number): string => {
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================
// Stats Card Component
// ============================================
const StatsCard: React.FC<{ title: string; value: number | string; subtitle?: string; color?: string }> = ({
  title,
  value,
  subtitle,
  color = 'primary.main'
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ============================================
// Overview Tab Component
// ============================================
const OverviewTab: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['public-social-stats'],
    queryFn: () => apiService.getPublicSocialStats(),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading stats</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard title="Total Accounts" value={stats?.accounts.total || 0} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard title="Active Accounts" value={stats?.accounts.active || 0} color="success.main" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard
          title="Instagram"
          value={stats?.accounts.byPlatform.instagram || 0}
          subtitle="accounts"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard
          title="TikTok"
          value={stats?.accounts.byPlatform.tiktok || 0}
          subtitle="accounts"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard
          title="Podcast Accounts"
          value={stats?.accounts.byType.podcast || 0}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard
          title="Program Accounts"
          value={stats?.accounts.byType.program || 0}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatsCard
          title="Total Content"
          value={stats?.content.total || 0}
          subtitle="scraped posts"
        />
      </Grid>
    </Grid>
  );
};

// ============================================
// Social Account Display Component
// ============================================
interface SocialAccountChipProps {
  account: PublicSocialAccount;
  onDelete: () => void;
}

const SocialAccountChip: React.FC<SocialAccountChipProps> = ({ account, onDelete }) => (
  <Chip
    size="small"
    icon={account.platform === 'instagram' ? <InstagramIcon sx={{ fontSize: 16 }} /> : <TikTokIcon />}
    label={`@${account.username}`}
    variant="outlined"
    onDelete={onDelete}
    onClick={() => {
      const url = account.platform === 'instagram'
        ? `https://instagram.com/${account.username}`
        : `https://tiktok.com/@${account.username}`;
      window.open(url, '_blank');
    }}
    sx={(theme) => ({
      cursor: 'pointer',
      borderColor:
        account.platform === 'instagram'
          ? '#E4405F'
          : theme.palette.mode === 'dark'
            ? theme.palette.text.secondary
            : '#000',
      '& .MuiChip-icon': {
        color:
          account.platform === 'instagram'
            ? '#E4405F'
            : theme.palette.mode === 'dark'
              ? theme.palette.text.secondary
              : '#000',
      },
      '&:hover': {
        backgroundColor:
          account.platform === 'instagram'
            ? alpha('#E4405F', 0.08)
            : alpha(theme.palette.text.primary, 0.08),
      },
    })}
  />
);

// ============================================
// Accounts Tab Component (Podcast-centric view)
// ============================================
const AccountsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [expandedPodcasts, setExpandedPodcasts] = useState<Set<string>>(new Set());

  // Use Zustand store for persisted filters
  const {
    dateFrom,
    dateTo,
    sortBy,
    sortOrder,
    search,
    setDateFrom,
    setDateTo,
    setSortBy,
    setSortOrder,
    setSearch,
  } = useSocialAdminStore();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState<{
    type: 'podcast' | 'program';
    id: string;
    name: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    platform: 'instagram' as 'instagram' | 'tiktok',
    username: '',
    isActive: true,
  });

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<{ id: string; name: string; podcastId: string } | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string>('');

  // Host dialog state
  const [hostDialogOpen, setHostDialogOpen] = useState(false);
  const [hostTarget, setHostTarget] = useState<{ type: 'podcast' | 'program'; id: string; name: string } | null>(null);
  const [hostSearchQuery, setHostSearchQuery] = useState('');
  const [newHostName, setNewHostName] = useState('');

  // Host social accounts dialog state
  const [hostSocialDialogOpen, setHostSocialDialogOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState<{ _id: string; name: string; linkedSocialAccounts: PublicSocialAccount[] } | null>(null);
  const [hostSocialUsername, setHostSocialUsername] = useState('');
  const [hostSocialPlatform, setHostSocialPlatform] = useState<'instagram' | 'tiktok'>('instagram');

  // Filter by platform: only show podcasts that have at least one account on this platform (IG or TikTok)
  const [platformFilter, setPlatformFilter] = useState<'all' | 'instagram' | 'tiktok'>('all');

  // Fetch podcasts with their programs and social accounts
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['podcasts-with-social', page, rowsPerPage, search, sortBy, sortOrder, dateFrom, dateTo],
    queryFn: () => apiService.getPodcastsWithSocial(page + 1, rowsPerPage, {
      search: search || undefined,
      sortBy,
      sortOrder,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof apiService.createPublicSocialAccount>[0]) =>
      apiService.createPublicSocialAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      queryClient.invalidateQueries({ queryKey: ['public-social-stats'] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deletePublicSocialAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      queryClient.invalidateQueries({ queryKey: ['public-social-stats'] });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      apiService.mergePrograms(sourceId, targetId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      alert(`${result.message}\n\nContent updated: ${result.contentUpdated}\nSocial accounts updated: ${result.socialAccountsUpdated}`);
      closeMergeDialog();
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const deletePodcastMutation = useMutation({
    mutationFn: (podcastId: string) => apiService.deletePodcast(podcastId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      queryClient.invalidateQueries({ queryKey: ['public-social-stats'] });
      const s3Info = result.deleted.s3Files > 0
        ? `\nS3 files deleted: ${result.deleted.s3Files}${result.deleted.s3FilesFailed > 0 ? ` (${result.deleted.s3FilesFailed} failed)` : ''}`
        : '';
      alert(`Podcast deleted!\n\nPrograms: ${result.deleted.programs}\nContent: ${result.deleted.content}\nSocial accounts: ${result.deleted.socialAccounts}${s3Info}`);
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Host search query
  const { data: guestSearchResults } = useQuery({
    queryKey: ['guest-search', hostSearchQuery],
    queryFn: () => apiService.searchGuests(hostSearchQuery, 10),
    enabled: hostSearchQuery.length >= 2,
  });

  const createGuestMutation = useMutation({
    mutationFn: (name: string) => apiService.createGuest({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-search'] });
      setNewHostName('');
    },
  });

  const assignHostMutation = useMutation({
    mutationFn: ({ guestId, type, targetId }: { guestId: string; type: 'podcast' | 'program'; targetId: string }) =>
      type === 'podcast'
        ? apiService.assignHostToPodcast(guestId, targetId)
        : apiService.assignHostToProgram(guestId, targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      closeHostDialog();
    },
  });

  const removeHostMutation = useMutation({
    mutationFn: ({ guestId, type, targetId }: { guestId: string; type: 'podcast' | 'program'; targetId: string }) =>
      type === 'podcast'
        ? apiService.removeHostFromPodcast(guestId, targetId)
        : apiService.removeHostFromProgram(guestId, targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
    },
  });

  const unlinkHostSocialMutation = useMutation({
    mutationFn: ({ guestId, socialAccountId }: { guestId: string; socialAccountId: string }) =>
      apiService.unlinkSocialAccountFromHost(guestId, socialAccountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      // Update the selected host's linked accounts
      if (selectedHost) {
        setSelectedHost({
          ...selectedHost,
          linkedSocialAccounts: selectedHost.linkedSocialAccounts.filter(a => a._id !== variables.socialAccountId),
        });
      }
      refetch();
    },
  });

  // Create social account for host and link it
  const createAndLinkSocialMutation = useMutation({
    mutationFn: async ({ guestId, platform, username }: { guestId: string; platform: 'instagram' | 'tiktok'; username: string }) => {
      // First create the social account (without podcast/program association - for host)
      const newAccount = await apiService.createPublicSocialAccount({
        platform,
        username,
        isActive: true,
      });
      // Then link it to the host
      await apiService.linkSocialAccountToHost(guestId, newAccount._id);
      return newAccount;
    },
    onSuccess: (newAccount) => {
      queryClient.invalidateQueries({ queryKey: ['podcasts-with-social'] });
      // Update the selectedHost state to show the new account in the dialog
      if (selectedHost) {
        setSelectedHost({
          ...selectedHost,
          linkedSocialAccounts: [...selectedHost.linkedSocialAccounts, newAccount],
        });
      }
      setHostSocialUsername('');
      refetch();
    },
  });

  const toggleExpanded = (podcastId: string) => {
    setExpandedPodcasts(prev => {
      const next = new Set(prev);
      if (next.has(podcastId)) {
        next.delete(podcastId);
      } else {
        next.add(podcastId);
      }
      return next;
    });
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogTarget(null);
    setFormData({
      platform: 'instagram',
      username: '',
      isActive: true,
    });
  };

  const openAddDialog = (type: 'podcast' | 'program', id: string, name: string) => {
    setDialogTarget({ type, id, name });
    setFormData({
      platform: 'instagram',
      username: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!dialogTarget || !formData.username) return;

    createMutation.mutate({
      platform: formData.platform,
      username: formData.username,
      accountType: dialogTarget.type,
      podcastId: dialogTarget.type === 'podcast' ? dialogTarget.id : undefined,
      programId: dialogTarget.type === 'program' ? dialogTarget.id : undefined,
      isActive: formData.isActive,
    });
  };

  const handleDeleteAccount = (accountId: string, username: string) => {
    if (window.confirm(`Remove @${username} from tracking?`)) {
      deleteMutation.mutate(accountId);
    }
  };

  const handleDeletePodcast = (podcastId: string, title: string, contentCount: number) => {
    const message = contentCount > 0
      ? `⚠️ WARNING: This will permanently delete "${title}" and all its data:\n\n• ${contentCount} content items\n• All programs\n• All social accounts\n\nThis action CANNOT be undone. Are you sure?`
      : `Delete "${title}"? This will remove the podcast and all related data.`;

    if (window.confirm(message)) {
      deletePodcastMutation.mutate(podcastId);
    }
  };

  const openMergeDialog = (id: string, name: string, podcastId: string) => {
    setMergeSource({ id, name, podcastId });
    setMergeTargetId('');
    setMergeDialogOpen(true);
  };

  const closeMergeDialog = () => {
    setMergeDialogOpen(false);
    setMergeSource(null);
    setMergeTargetId('');
  };

  const handleMerge = () => {
    if (!mergeSource || !mergeTargetId) return;
    if (!window.confirm(`Merge "${mergeSource.name}" into selected program? All content will be moved.`)) return;
    mergeMutation.mutate({ sourceId: mergeSource.id, targetId: mergeTargetId });
  };

  // Get all programs for merge target selection (from same podcast)
  const getMergeTargetOptions = () => {
    if (!mergeSource || !data?.podcasts) return [];
    const podcast = data.podcasts.find(p => p._id === mergeSource.podcastId);
    if (!podcast) return [];
    return podcast.programs.filter(p => p._id !== mergeSource.id);
  };

  const openHostDialog = (type: 'podcast' | 'program', id: string, name: string) => {
    setHostTarget({ type, id, name });
    setHostSearchQuery('');
    setNewHostName('');
    setHostDialogOpen(true);
  };

  const openHostSocialDialog = (host: { _id: string; name: string; linkedSocialAccounts: PublicSocialAccount[] }) => {
    setSelectedHost(host);
    setHostSocialUsername('');
    setHostSocialPlatform('instagram');
    setHostSocialDialogOpen(true);
  };

  const closeHostSocialDialog = () => {
    setHostSocialDialogOpen(false);
    setSelectedHost(null);
    setHostSocialUsername('');
  };

  const handleAddHostSocial = () => {
    if (!selectedHost || !hostSocialUsername.trim()) return;
    createAndLinkSocialMutation.mutate({
      guestId: selectedHost._id,
      platform: hostSocialPlatform,
      username: hostSocialUsername.trim().replace('@', ''),
    });
  };

  const handleUnlinkHostSocial = (socialAccountId: string) => {
    if (!selectedHost) return;
    unlinkHostSocialMutation.mutate({ guestId: selectedHost._id, socialAccountId });
  };

  const closeHostDialog = () => {
    setHostDialogOpen(false);
    setHostTarget(null);
    setHostSearchQuery('');
    setNewHostName('');
  };

  const handleAssignHost = (guestId: string) => {
    if (!hostTarget) return;
    assignHostMutation.mutate({ guestId, type: hostTarget.type, targetId: hostTarget.id });
  };

  const handleRemoveHost = (guestId: string, type: 'podcast' | 'program', targetId: string) => {
    if (!window.confirm('Remove this host?')) return;
    removeHostMutation.mutate({ guestId, type, targetId });
  };

  const handleCreateAndAssignHost = () => {
    if (!newHostName.trim() || !hostTarget) return;
    createGuestMutation.mutate(newHostName.trim(), {
      onSuccess: (newGuest) => {
        assignHostMutation.mutate({ guestId: newGuest._id, type: hostTarget.type, targetId: hostTarget.id });
      }
    });
  };

  const rawPodcasts = data?.podcasts || [];

  // Helper: account has platform (case-insensitive)
  const hasPlatform = (account: { platform?: string }, platform: string) =>
    (account.platform || '').toLowerCase() === platform;

  // Filter by platform when "Instagram" or "TikTok" is selected: show only podcasts that have at least one account on that platform (podcast-level or any program)
  const podcasts =
    platformFilter === 'all'
      ? rawPodcasts
      : rawPodcasts.filter((podcast) => {
          const podcastIg = (podcast.assignedSocialAccounts || []).some((a) => hasPlatform(a, 'instagram'));
          const podcastTk = (podcast.assignedSocialAccounts || []).some((a) => hasPlatform(a, 'tiktok'));
          const programIg = (podcast.programs || []).some((p) =>
            (p.assignedSocialAccounts || []).some((a) => hasPlatform(a, 'instagram'))
          );
          const programTk = (podcast.programs || []).some((p) =>
            (p.assignedSocialAccounts || []).some((a) => hasPlatform(a, 'tiktok'))
          );
          if (platformFilter === 'instagram') return podcastIg || programIg;
          if (platformFilter === 'tiktok') return podcastTk || programTk;
          return true;
        });

  const total = data?.pagination?.total || 0;

  return (
    <>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search podcasts..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Show</InputLabel>
                <Select
                  value={platformFilter}
                  label="Show"
                  onChange={(e) => {
                    setPlatformFilter(e.target.value as 'all' | 'instagram' | 'tiktok');
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="instagram">Has Instagram</MenuItem>
                  <MenuItem value="tiktok">Has TikTok</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value as any);
                    setPage(0);
                  }}
                >
                  <MenuItem value="title">Name</MenuItem>
                  <MenuItem value="views">Views</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => {
                    setSortOrder(e.target.value as any);
                    setPage(0);
                  }}
                >
                  <MenuItem value="asc">↑ Asc</MenuItem>
                  <MenuItem value="desc">↓ Desc</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 'auto' }} sx={{ ml: 'auto' }}>
              <Stack direction="row" spacing={1}>
                {(dateFrom || dateTo) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setPage(0);
                    }}
                  >
                    Clear Dates
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                >
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Podcasts Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.neutral[200] : theme.palette.grey[100],
                })}
              >
                <TableCell sx={{ width: 50 }}></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Podcast / Program</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Views</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hosts</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Instagram</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>TikTok</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Alert severity="error">Error loading podcasts</Alert>
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                // Show skeleton rows when loading
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell><Skeleton width={40} height={40} variant="circular" /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box>
                          <Skeleton width={200} height={20} />
                          <Skeleton width={150} height={16} sx={{ mt: 0.5 }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={60} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell align="center"><Skeleton width={80} /></TableCell>
                  </TableRow>
                ))
              ) : podcasts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No podcasts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                podcasts.map((podcast) => {
                  const isExpanded = expandedPodcasts.has(podcast._id);
                  const hasPrograms = podcast.programs && podcast.programs.length > 0;

                  // Get Instagram and TikTok accounts for the podcast (case-insensitive platform check)
                  const podcastInstagram = podcast.assignedSocialAccounts?.filter(a => hasPlatform(a, 'instagram')) || [];
                  const podcastTikTok = podcast.assignedSocialAccounts?.filter(a => hasPlatform(a, 'tiktok')) || [];

                  // Also show legacy socialAccounts field if no assigned accounts
                  const legacyInstagram = podcast.socialAccounts?.instagram;
                  const legacyTikTok = podcast.socialAccounts?.tiktok;

                  return (
                    <React.Fragment key={podcast._id}>
                      {/* Podcast Row */}
                      <TableRow
                        hover
                        sx={(theme) => ({
                          '& > *': { borderBottom: hasPrograms && isExpanded ? 'none' : undefined },
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.paper
                              : theme.palette.grey[50],
                        })}
                      >
                        <TableCell>
                          {hasPrograms && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(podcast._id)}
                            >
                              {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              src={podcast.imageS3Key
                                ? ensureDirectS3Url(getS3Url(podcast.imageS3Key))
                                : podcast.imageUrl}
                              sx={{ width: 40, height: 40 }}
                            >
                              <PodcastsIcon />
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {podcast.title}
                                </Typography>
                                {podcast.hasLivestreamChannel ? (
                                  <Chip
                                    icon={<LiveTvIcon sx={{ fontSize: 14 }} />}
                                    label="Live"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      backgroundColor: '#ff5252',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' },
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    icon={<PodcastsIcon sx={{ fontSize: 14 }} />}
                                    label="Podcast"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      backgroundColor: '#9c27b0',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' },
                                    }}
                                  />
                                )}
                              </Box>
                              {podcast.author && (
                                <Typography variant="caption" color="text.secondary">
                                  {podcast.author}
                                </Typography>
                              )}
                              {hasPrograms && (
                                <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                                  {podcast.programs.length} program{podcast.programs.length !== 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {(podcast.category || podcast.type) && (
                            <Stack direction="row" spacing={0.5}>
                              {podcast.category && (
                                <Chip
                                  label={podcast.category}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                    backgroundColor:
                                      podcast.category === 'politics' ? '#2196f3' : '#757575',
                                    color: 'white',
                                  }}
                                />
                              )}
                              {podcast.type && (
                                <Chip
                                  label={podcast.type}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: 11, textTransform: 'capitalize' }}
                                />
                              )}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {podcast.stats?.views?.toLocaleString() || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {podcast.stats?.contentCount || 0} videos
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                            {(podcast as any).hosts?.map((host: any) => (
                              <Chip
                                key={host._id}
                                size="small"
                                icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                label={`${host.name}${host.linkedSocialAccounts?.length ? ` (${host.linkedSocialAccounts.length})` : ''}`}
                                onClick={() => openHostSocialDialog(host)}
                                onDelete={() => handleRemoveHost(host._id, 'podcast', podcast._id)}
                                sx={(theme) => ({
                                  cursor: 'pointer',
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'dark' ? 0.14 : 0.09,
                                  ),
                                  '&:hover': {
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === 'dark' ? 0.22 : 0.15,
                                    ),
                                  },
                                })}
                              />
                            ))}
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openHostDialog('podcast', podcast._id, podcast.title)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {podcastInstagram.map(account => (
                              <SocialAccountChip
                                key={account._id}
                                account={account}
                                onDelete={() => handleDeleteAccount(account._id, account.username)}
                              />
                            ))}
                            {podcastInstagram.length === 0 && legacyInstagram && (
                              <Chip
                                size="small"
                                icon={<InstagramIcon sx={{ fontSize: 16 }} />}
                                label={`@${legacyInstagram}`}
                                variant="outlined"
                                sx={{ borderColor: '#E4405F', opacity: 0.6 }}
                                onClick={() => window.open(`https://instagram.com/${legacyInstagram}`, '_blank')}
                              />
                            )}
                            {podcastInstagram.length === 0 && !legacyInstagram && (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {podcastTikTok.map(account => (
                              <SocialAccountChip
                                key={account._id}
                                account={account}
                                onDelete={() => handleDeleteAccount(account._id, account.username)}
                              />
                            ))}
                            {podcastTikTok.length === 0 && legacyTikTok && (
                              <Chip
                                size="small"
                                icon={<TikTokIcon />}
                                label={`@${legacyTikTok}`}
                                variant="outlined"
                                sx={{ borderColor: '#000', opacity: 0.6 }}
                                onClick={() => window.open(`https://tiktok.com/@${legacyTikTok}`, '_blank')}
                              />
                            )}
                            {podcastTikTok.length === 0 && !legacyTikTok && (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Add Account">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openAddDialog('podcast', podcast._id, podcast.title)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Podcast">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeletePodcast(podcast._id, podcast.title, podcast.stats?.contentCount || 0)}
                                disabled={deletePodcastMutation.isPending}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Programs Rows (Collapsible) */}
                      {hasPrograms && isExpanded && podcast.programs.map((program) => {
                        const programInstagram = program.assignedSocialAccounts?.filter(a => hasPlatform(a, 'instagram')) || [];
                        const programTikTok = program.assignedSocialAccounts?.filter(a => hasPlatform(a, 'tiktok')) || [];
                        const legacyProgramInstagram = program.socialAccounts?.instagram;
                        const legacyProgramTikTok = program.socialAccounts?.tiktok;

                        return (
                          <TableRow
                            key={program._id}
                            hover
                            sx={(theme) => ({
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.neutral[100]
                                  : theme.palette.background.paper,
                            })}
                          >
                            <TableCell sx={{ width: 50 }}></TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 4 }}>
                                <Avatar
                                  src={program.imageS3Key
                                    ? ensureDirectS3Url(getS3Url(program.imageS3Key))
                                    : podcast.imageS3Key
                                      ? ensureDirectS3Url(getS3Url(podcast.imageS3Key))
                                      : podcast.imageUrl}
                                  sx={{ width: 32, height: 32 }}
                                />
                                <Box>
                                  <Typography variant="body2">
                                    {program.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Program
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {program.stats?.views?.toLocaleString() || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                                {program.hosts?.map((host: any) => (
                                  <Chip
                                    key={host._id}
                                    size="small"
                                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                    label={`${host.name}${host.linkedSocialAccounts?.length ? ` (${host.linkedSocialAccounts.length})` : ''}`}
                                    onClick={() => openHostSocialDialog(host)}
                                    onDelete={() => handleRemoveHost(host._id, 'program', program._id)}
                                    sx={(theme) => ({
                                      cursor: 'pointer',
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.14 : 0.09,
                                      ),
                                      '&:hover': {
                                        backgroundColor: alpha(
                                          theme.palette.primary.main,
                                          theme.palette.mode === 'dark' ? 0.22 : 0.15,
                                        ),
                                      },
                                    })}
                                  />
                                ))}
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openHostDialog('program', program._id, program.name)}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {programInstagram.map(account => (
                                  <SocialAccountChip
                                    key={account._id}
                                    account={account}
                                    onDelete={() => handleDeleteAccount(account._id, account.username)}
                                  />
                                ))}
                                {programInstagram.length === 0 && legacyProgramInstagram && (
                                  <Chip
                                    size="small"
                                    icon={<InstagramIcon sx={{ fontSize: 16 }} />}
                                    label={`@${legacyProgramInstagram}`}
                                    variant="outlined"
                                    sx={{ borderColor: '#E4405F', opacity: 0.6 }}
                                    onClick={() => window.open(`https://instagram.com/${legacyProgramInstagram}`, '_blank')}
                                  />
                                )}
                                {programInstagram.length === 0 && !legacyProgramInstagram && (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {programTikTok.map(account => (
                                  <SocialAccountChip
                                    key={account._id}
                                    account={account}
                                    onDelete={() => handleDeleteAccount(account._id, account.username)}
                                  />
                                ))}
                                {programTikTok.length === 0 && legacyProgramTikTok && (
                                  <Chip
                                    size="small"
                                    icon={<TikTokIcon />}
                                    label={`@${legacyProgramTikTok}`}
                                    variant="outlined"
                                    sx={{ borderColor: '#000', opacity: 0.6 }}
                                    onClick={() => window.open(`https://tiktok.com/@${legacyProgramTikTok}`, '_blank')}
                                  />
                                )}
                                {programTikTok.length === 0 && !legacyProgramTikTok && (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Add Account">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openAddDialog('program', program._id, program.name)}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Merge into another program">
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => openMergeDialog(program._id, program.name, podcast._id)}
                                  >
                                    <MergeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Add Account Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Social Account to {dialogTarget?.name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={formData.platform}
                label="Platform"
                onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
              >
                <MenuItem value="instagram">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InstagramIcon sx={{ color: '#E4405F' }} />
                    Instagram
                  </Box>
                </MenuItem>
                <MenuItem value="tiktok">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TikTokIcon />
                    TikTok
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
              placeholder="e.g., vaboricua"
              helperText="Without the @ symbol"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">@</InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active (will be scraped)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.username || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Programs Dialog */}
      <Dialog open={mergeDialogOpen} onClose={closeMergeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MergeIcon color="secondary" />
            Merge Program
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This will move all content and social accounts from "<strong>{mergeSource?.name}</strong>" to the selected target program. This action cannot be undone.
            </Alert>

            <TextField
              label="Source Program"
              value={mergeSource?.name || ''}
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Target Program</InputLabel>
              <Select
                value={mergeTargetId}
                label="Target Program"
                onChange={(e) => setMergeTargetId(e.target.value)}
              >
                {getMergeTargetOptions().map((program) => (
                  <MenuItem key={program._id} value={program._id}>
                    {program.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMergeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleMerge}
            disabled={!mergeTargetId || mergeMutation.isPending}
          >
            {mergeMutation.isPending ? 'Merging...' : 'Merge Programs'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Host Assignment Dialog */}
      <Dialog open={hostDialogOpen} onClose={closeHostDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Assign Host to {hostTarget?.type === 'podcast' ? 'Podcast' : 'Program'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Assigning host to: <strong>{hostTarget?.name}</strong>
            </Typography>

            <TextField
              label="Search existing hosts"
              value={hostSearchQuery}
              onChange={(e) => setHostSearchQuery(e.target.value)}
              fullWidth
              placeholder="Type at least 2 characters..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {guestSearchResults?.guests && guestSearchResults.guests.length > 0 && (
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                {guestSearchResults.guests.map((guest) => (
                  <Box
                    key={guest._id}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleAssignHost(guest._id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" />
                      <Typography variant="body2">{guest.name}</Typography>
                    </Box>
                    <Button size="small" variant="outlined">
                      Assign
                    </Button>
                  </Box>
                ))}
              </Paper>
            )}

            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or create a new host:
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="New host name"
                  value={newHostName}
                  onChange={(e) => setNewHostName(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g., Juan Pérez"
                />
                <Button
                  variant="contained"
                  onClick={handleCreateAndAssignHost}
                  disabled={!newHostName.trim() || createGuestMutation.isPending || assignHostMutation.isPending}
                >
                  Create & Assign
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHostDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Host Social Accounts Dialog */}
      <Dialog open={hostSocialDialogOpen} onClose={closeHostSocialDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Manage Social Accounts for {selectedHost?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Current linked accounts */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Linked Social Accounts
              </Typography>
              {selectedHost?.linkedSocialAccounts && selectedHost.linkedSocialAccounts.length > 0 ? (
                <Stack spacing={1}>
                  {selectedHost.linkedSocialAccounts.map((account) => (
                    <Paper
                      key={account._id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {account.platform === 'instagram' ? (
                          <InstagramIcon sx={{ color: '#E4405F' }} />
                        ) : (
                          <TikTokIcon />
                        )}
                        <Typography variant="body2">@{account.username}</Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleUnlinkHostSocial(account._id)}
                        disabled={unlinkHostSocialMutation.isPending}
                      >
                        Unlink
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No social accounts linked yet.
                </Typography>
              )}
            </Box>

            {/* Add new social account */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Add New Social Account
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={hostSocialPlatform}
                    label="Platform"
                    onChange={(e) => setHostSocialPlatform(e.target.value as 'instagram' | 'tiktok')}
                  >
                    <MenuItem value="instagram">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InstagramIcon sx={{ fontSize: 18, color: '#E4405F' }} />
                        Instagram
                      </Box>
                    </MenuItem>
                    <MenuItem value="tiktok">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TikTokIcon />
                        TikTok
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Username"
                  value={hostSocialUsername}
                  onChange={(e) => setHostSocialUsername(e.target.value)}
                  size="small"
                  placeholder="e.g., username"
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">@</InputAdornment>,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddHostSocial}
                  disabled={!hostSocialUsername.trim() || createAndLinkSocialMutation.isPending}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHostSocialDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================
// Hosts Tab Component (Host-centric view for adding social accounts)
// ============================================
const HostsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchInput, setSearchInput] = useState(''); // For the input field
  const [search, setSearch] = useState(''); // Debounced value for API
  const [filterBySocial, setFilterBySocial] = useState<'all' | 'with' | 'without'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'podcast' | 'program'>('name');

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Dialog state for managing host social accounts
  const [selectedHost, setSelectedHost] = useState<{
    _id: string;
    name: string;
    linkedSocialAccounts: Array<{ _id: string; platform: string; username: string }>;
  } | null>(null);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [newSocialPlatform, setNewSocialPlatform] = useState<'instagram' | 'tiktok'>('instagram');
  const [newSocialUsername, setNewSocialUsername] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHost, setEditHost] = useState<{
    _id: string;
    name: string;
    assignedPodcasts: Array<{ _id: string; title: string }>;
    assignedPrograms: Array<{ _id: string; name: string }>;
  } | null>(null);
  const [editName, setEditName] = useState('');
  const [selectedPodcastToAdd, setSelectedPodcastToAdd] = useState('');
  const [selectedProgramToAdd, setSelectedProgramToAdd] = useState('');

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSourceHost, setMergeSourceHost] = useState<{ _id: string; name: string } | null>(null);
  const [mergeTargetHostId, setMergeTargetHostId] = useState('');
  const [mergeSearchInput, setMergeSearchInput] = useState('');
  const [mergeSearchQuery, setMergeSearchQuery] = useState('');

  // Debounce merge search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setMergeSearchQuery(mergeSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [mergeSearchInput]);

  // Create host dialog state
  const [createHostDialogOpen, setCreateHostDialogOpen] = useState(false);
  const [newHostName, setNewHostName] = useState('');

  // Fetch hosts
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['all-hosts', page, rowsPerPage, search, filterBySocial, sortBy],
    queryFn: () => apiService.getAllHosts(page + 1, rowsPerPage, {
      search: search || undefined,
      filterBySocial: filterBySocial === 'all' ? undefined : filterBySocial,
      sortBy: sortBy,
    }),
  });

  // Fetch available podcasts and programs for assignment
  const { data: assignmentOptions } = useQuery({
    queryKey: ['host-assignment-options'],
    queryFn: () => apiService.getHostAssignmentOptions(),
    enabled: editDialogOpen, // Only fetch when edit dialog is open
  });

  // Fetch hosts for merge dialog (searches all hosts, not just current page)
  const { data: mergeHostsData, isLoading: mergeHostsLoading } = useQuery({
    queryKey: ['merge-hosts-search', mergeSearchQuery],
    queryFn: () => apiService.getAllHosts(1, 50, {
      search: mergeSearchQuery || undefined,
    }),
    enabled: mergeDialogOpen, // Only fetch when merge dialog is open
  });

  // Filter merge hosts to exclude source host
  const mergeHosts = (mergeHostsData?.hosts || []).filter(
    (h) => h._id !== mergeSourceHost?._id
  );

  // Create and link social account mutation
  const createAndLinkMutation = useMutation({
    mutationFn: async ({ guestId, platform, username }: { guestId: string; platform: 'instagram' | 'tiktok'; username: string }) => {
      console.log('Creating social account:', { platform, username });
      const newAccount = await apiService.createPublicSocialAccount({
        platform,
        username,
        isActive: true,
      });
      console.log('Account created/found:', newAccount);
      console.log('Linking to host:', guestId);
      const linkResult = await apiService.linkSocialAccountToHost(guestId, newAccount._id);
      console.log('Link result:', linkResult);
      return newAccount;
    },
    onSuccess: (newAccount) => {
      console.log('Mutation succeeded, updating UI');
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      // Update the selectedHost state to show the new account in the dialog
      if (selectedHost) {
        setSelectedHost({
          ...selectedHost,
          linkedSocialAccounts: [...selectedHost.linkedSocialAccounts, newAccount],
        });
      }
      setNewSocialUsername('');
      refetch();
    },
    onError: (error: any) => {
      console.error('Mutation failed:', error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Unlink social account mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ guestId, socialAccountId }: { guestId: string; socialAccountId: string }) =>
      apiService.unlinkSocialAccountFromHost(guestId, socialAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      refetch();
    },
  });

  // Update guest mutation
  const updateGuestMutation = useMutation({
    mutationFn: ({ guestId, name }: { guestId: string; name: string }) =>
      apiService.updateGuest(guestId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      closeEditDialog();
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Delete guest mutation
  const deleteGuestMutation = useMutation({
    mutationFn: (guestId: string) => apiService.deleteGuest(guestId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      alert(`Deleted "${result.deleted.name}"`);
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Merge guests mutation
  const mergeGuestsMutation = useMutation({
    mutationFn: ({ sourceGuestId, targetGuestId }: { sourceGuestId: string; targetGuestId: string }) =>
      apiService.mergeGuests(sourceGuestId, targetGuestId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      closeMergeDialog();
      alert(`Merged "${result.merged.sourceName}" into "${result.merged.targetName}"`);
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Remove host from program mutation
  const removeFromProgramMutation = useMutation({
    mutationFn: ({ guestId, programId }: { guestId: string; programId: string }) =>
      apiService.removeHostFromProgram(guestId, programId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      // Update the editHost state to remove the program locally
      if (editHost) {
        setEditHost({
          ...editHost,
          assignedPrograms: editHost.assignedPrograms.filter((p) => p._id !== removeFromProgramMutation.variables?.programId),
        });
      }
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Remove host from podcast mutation
  const removeFromPodcastMutation = useMutation({
    mutationFn: ({ guestId, podcastId }: { guestId: string; podcastId: string }) =>
      apiService.removeHostFromPodcast(guestId, podcastId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      // Update the editHost state to remove the podcast locally
      if (editHost) {
        setEditHost({
          ...editHost,
          assignedPodcasts: editHost.assignedPodcasts.filter((p) => p._id !== removeFromPodcastMutation.variables?.podcastId),
        });
      }
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Assign host to podcast mutation
  const assignToPodcastMutation = useMutation({
    mutationFn: ({ guestId, podcastId }: { guestId: string; podcastId: string }) =>
      apiService.assignHostToPodcast(guestId, podcastId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      // Update the editHost state to add the podcast locally
      if (editHost && assignmentOptions) {
        const podcast = assignmentOptions.podcasts.find(p => p._id === variables.podcastId);
        if (podcast) {
          setEditHost({
            ...editHost,
            assignedPodcasts: [...editHost.assignedPodcasts, { _id: podcast._id, title: podcast.title }],
          });
        }
      }
      setSelectedPodcastToAdd('');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Assign host to program mutation
  const assignToProgramMutation = useMutation({
    mutationFn: ({ guestId, programId }: { guestId: string; programId: string }) =>
      apiService.assignHostToProgram(guestId, programId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      // Update the editHost state to add the program locally
      if (editHost && assignmentOptions) {
        const program = assignmentOptions.programs.find(p => p._id === variables.programId);
        if (program) {
          setEditHost({
            ...editHost,
            assignedPrograms: [...editHost.assignedPrograms, { _id: program._id, name: program.name }],
          });
        }
      }
      setSelectedProgramToAdd('');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Create new host mutation
  const createHostMutation = useMutation({
    mutationFn: (name: string) => apiService.createGuest({ name }),
    onSuccess: (newHost) => {
      queryClient.invalidateQueries({ queryKey: ['all-hosts'] });
      setCreateHostDialogOpen(false);
      setNewHostName('');
      // Open edit dialog for the new host
      setEditHost({
        _id: newHost._id,
        name: newHost.name,
        assignedPodcasts: [],
        assignedPrograms: [],
      });
      setEditName(newHost.name);
      setEditDialogOpen(true);
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const openSocialDialog = (host: typeof selectedHost) => {
    setSelectedHost(host);
    setNewSocialUsername('');
    setNewSocialPlatform('instagram');
    setSocialDialogOpen(true);
  };

  const closeSocialDialog = () => {
    setSocialDialogOpen(false);
    setSelectedHost(null);
  };

  const handleAddSocial = () => {
    if (!selectedHost || !newSocialUsername.trim()) return;
    createAndLinkMutation.mutate({
      guestId: selectedHost._id,
      platform: newSocialPlatform,
      username: newSocialUsername.trim().replace('@', ''),
    });
  };

  const handleUnlinkSocial = (socialAccountId: string) => {
    if (!selectedHost) return;
    unlinkMutation.mutate({ guestId: selectedHost._id, socialAccountId });
  };

  const openEditDialog = (host: {
    _id: string;
    name: string;
    assignedPodcasts: Array<{ _id: string; title: string }>;
    assignedPrograms: Array<{ _id: string; name: string }>;
  }) => {
    setEditHost(host);
    setEditName(host.name);
    setSelectedPodcastToAdd('');
    setSelectedProgramToAdd('');
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditHost(null);
    setEditName('');
  };

  const handleSaveEdit = () => {
    if (!editHost || !editName.trim()) return;
    updateGuestMutation.mutate({ guestId: editHost._id, name: editName.trim() });
  };

  const handleDeleteHost = (host: { _id: string; name: string }) => {
    if (window.confirm(`Delete "${host.name}"? This will remove the host from all podcasts and programs.`)) {
      deleteGuestMutation.mutate(host._id);
    }
  };

  const openMergeDialog = (host: { _id: string; name: string }) => {
    setMergeSourceHost(host);
    setMergeTargetHostId('');
    setMergeSearchInput('');
    setMergeSearchQuery('');
    setMergeDialogOpen(true);
  };

  const closeMergeDialog = () => {
    setMergeDialogOpen(false);
    setMergeSourceHost(null);
    setMergeTargetHostId('');
    setMergeSearchInput('');
    setMergeSearchQuery('');
  };

  const handleMerge = () => {
    if (!mergeSourceHost || !mergeTargetHostId) return;
    mergeGuestsMutation.mutate({
      sourceGuestId: mergeSourceHost._id,
      targetGuestId: mergeTargetHostId,
    });
  };

  const hosts = data?.hosts || [];
  const total = data?.pagination?.total || 0;


  return (
    <>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search hosts..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Social</InputLabel>
                <Select
                  value={filterBySocial}
                  label="Social"
                  onChange={(e) => {
                    setFilterBySocial(e.target.value as any);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="without">Without</MenuItem>
                  <MenuItem value="with">With</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'podcast' | 'program')}
                >
                  <MenuItem value="name">Host Name</MenuItem>
                  <MenuItem value="podcast">Podcast</MenuItem>
                  <MenuItem value="program">Program</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 'auto' }} sx={{ ml: 'auto' }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewHostName('');
                    setCreateHostDialogOpen(true);
                  }}
                >
                  Create Host
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Hosts Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.neutral[200] : theme.palette.grey[100],
                })}
              >
                <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Podcasts / Programs</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Instagram</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>TikTok</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Content</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Views</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Likes</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Alert severity="error">Error loading hosts</Alert>
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                // Show skeleton rows when loading
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box>
                          <Skeleton width={150} height={20} />
                          <Skeleton width={100} height={16} sx={{ mt: 0.5 }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell align="right"><Skeleton width={60} /></TableCell>
                    <TableCell align="right"><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={80} /></TableCell>
                    <TableCell align="center"><Skeleton width={100} /></TableCell>
                  </TableRow>
                ))
              ) : hosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hosts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                hosts.map((host) => {
                  const instagramAccounts = host.linkedSocialAccounts.filter((a: any) => a.platform === 'instagram');
                  const tiktokAccounts = host.linkedSocialAccounts.filter((a: any) => a.platform === 'tiktok');

                  return (
                    <TableRow
                      key={host._id}
                      hover
                      sx={(theme) => ({
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.paper
                            : theme.palette.grey[50],
                      })}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {host.name}
                            </Typography>
                            {!host.hasSocialAccounts && (
                              <Typography variant="caption" color="error.main">
                                No social accounts
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {host.assignedPodcasts.map((podcast) => (
                            <Chip
                              key={podcast._id}
                              size="small"
                              icon={<PodcastsIcon sx={{ fontSize: 16 }} />}
                              label={podcast.title}
                              sx={(theme) => ({
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === 'dark' ? 0.14 : 0.09,
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'dark' ? 0.22 : 0.15,
                                  ),
                                },
                              })}
                            />
                          ))}
                          {host.assignedPrograms.map((program) => (
                            <Chip
                              key={program._id}
                              size="small"
                              label={program.name}
                              sx={(theme) => ({
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.neutral[300]
                                    : theme.palette.grey[100],
                              })}
                            />
                          ))}
                          {host.assignedPodcasts.length === 0 && host.assignedPrograms.length === 0 && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {instagramAccounts.map((account: any) => (
                            <Chip
                              key={account._id}
                              size="small"
                              icon={<InstagramIcon sx={{ fontSize: 16 }} />}
                              label={`@${account.username}`}
                              variant="outlined"
                              onClick={() => window.open(`https://instagram.com/${account.username}`, '_blank')}
                              sx={{
                                cursor: 'pointer',
                                borderColor: '#E4405F',
                                '& .MuiChip-icon': { color: '#E4405F' },
                                '&:hover': { backgroundColor: 'rgba(228, 64, 95, 0.08)' },
                              }}
                            />
                          ))}
                          {instagramAccounts.length === 0 && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {tiktokAccounts.map((account: any) => (
                            <Chip
                              key={account._id}
                              size="small"
                              icon={<TikTokIcon />}
                              label={`@${account.username}`}
                              variant="outlined"
                              onClick={() => window.open(`https://tiktok.com/@${account.username}`, '_blank')}
                              sx={{
                                cursor: 'pointer',
                                borderColor: '#000',
                                '& .MuiChip-icon': { color: '#000' },
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                              }}
                            />
                          ))}
                          {tiktokAccounts.length === 0 && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={(host.contentCount || 0).toLocaleString()}>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCompactNumber(host.contentCount || 0)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={(host.totalViews || 0).toLocaleString()}>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCompactNumber(host.totalViews || 0)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={(host.totalLikes || 0).toLocaleString()}>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCompactNumber(host.totalLikes || 0)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Add Social Account">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openSocialDialog({
                                _id: host._id,
                                name: host.name,
                                linkedSocialAccounts: host.linkedSocialAccounts,
                              })}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Host">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog({
                                _id: host._id,
                                name: host.name,
                                assignedPodcasts: host.assignedPodcasts || [],
                                assignedPrograms: host.assignedPrograms || [],
                              })}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Host">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteHost({ _id: host._id, name: host.name })}
                              disabled={deleteGuestMutation.isPending}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Merge Into Another Host">
                            <IconButton
                              size="small"
                              onClick={() => openMergeDialog({ _id: host._id, name: host.name })}
                            >
                              <MergeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Manage Social Accounts Dialog */}
      <Dialog open={socialDialogOpen} onClose={closeSocialDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Social Accounts for {selectedHost?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Current linked accounts */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Linked Social Accounts
              </Typography>
              {selectedHost?.linkedSocialAccounts && selectedHost.linkedSocialAccounts.length > 0 ? (
                <Stack spacing={1}>
                  {selectedHost.linkedSocialAccounts.map((account: any) => (
                    <Paper
                      key={account._id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {account.platform === 'instagram' ? (
                          <InstagramIcon sx={{ color: '#E4405F' }} />
                        ) : (
                          <TikTokIcon />
                        )}
                        <Typography variant="body2">@{account.username}</Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleUnlinkSocial(account._id)}
                        disabled={unlinkMutation.isPending}
                      >
                        Unlink
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No social accounts linked yet.
                </Typography>
              )}
            </Box>

            {/* Add new social account */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Add New Social Account
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={newSocialPlatform}
                    label="Platform"
                    onChange={(e) => setNewSocialPlatform(e.target.value as 'instagram' | 'tiktok')}
                  >
                    <MenuItem value="instagram">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InstagramIcon sx={{ fontSize: 18, color: '#E4405F' }} />
                        Instagram
                      </Box>
                    </MenuItem>
                    <MenuItem value="tiktok">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TikTokIcon />
                        TikTok
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Username"
                  value={newSocialUsername}
                  onChange={(e) => setNewSocialUsername(e.target.value)}
                  size="small"
                  placeholder="e.g., username"
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">@</InputAdornment>,
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddSocial();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddSocial}
                  disabled={!newSocialUsername.trim() || createAndLinkMutation.isPending}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSocialDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Host Dialog */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Host</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveEdit();
                }
              }}
            />

            {/* Assigned Podcasts */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Assigned Podcasts
              </Typography>
              {editHost?.assignedPodcasts && editHost.assignedPodcasts.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {editHost.assignedPodcasts.map((podcast) => (
                    <Paper
                      key={podcast._id}
                      variant="outlined"
                      sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PodcastsIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                        <Typography variant="body2">{podcast.title}</Typography>
                      </Box>
                      <Tooltip title="Remove from podcast">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFromPodcastMutation.mutate({ guestId: editHost._id, podcastId: podcast._id })}
                          disabled={removeFromPodcastMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))}
                </Stack>
              )}
              {/* Add podcast */}
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Add Podcast</InputLabel>
                  <Select
                    value={selectedPodcastToAdd}
                    label="Add Podcast"
                    onChange={(e) => setSelectedPodcastToAdd(e.target.value)}
                  >
                    {assignmentOptions?.podcasts
                      .filter(p => !editHost?.assignedPodcasts.some(ap => ap._id === p._id))
                      .map(podcast => (
                        <MenuItem key={podcast._id} value={podcast._id}>
                          {podcast.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (editHost && selectedPodcastToAdd) {
                      assignToPodcastMutation.mutate({ guestId: editHost._id, podcastId: selectedPodcastToAdd });
                    }
                  }}
                  disabled={!selectedPodcastToAdd || assignToPodcastMutation.isPending}
                >
                  Add
                </Button>
              </Stack>
            </Box>

            {/* Assigned Programs */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Assigned Programs
              </Typography>
              {editHost?.assignedPrograms && editHost.assignedPrograms.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {editHost.assignedPrograms.map((program) => (
                    <Paper
                      key={program._id}
                      variant="outlined"
                      sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2">{program.name}</Typography>
                      <Tooltip title="Remove from program">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFromProgramMutation.mutate({ guestId: editHost._id, programId: program._id })}
                          disabled={removeFromProgramMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))}
                </Stack>
              )}
              {/* Add program */}
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Add Program</InputLabel>
                  <Select
                    value={selectedProgramToAdd}
                    label="Add Program"
                    onChange={(e) => setSelectedProgramToAdd(e.target.value)}
                  >
                    {assignmentOptions?.programs
                      .filter(p => !editHost?.assignedPrograms.some(ap => ap._id === p._id))
                      .map(program => (
                        <MenuItem key={program._id} value={program._id}>
                          {program.name} ({program.podcastTitle})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (editHost && selectedProgramToAdd) {
                      assignToProgramMutation.mutate({ guestId: editHost._id, programId: selectedProgramToAdd });
                    }
                  }}
                  disabled={!selectedProgramToAdd || assignToProgramMutation.isPending}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editName.trim() || editName === editHost?.name || updateGuestMutation.isPending}
          >
            {updateGuestMutation.isPending ? 'Saving...' : 'Save Name'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Host Dialog */}
      <Dialog open={mergeDialogOpen} onClose={closeMergeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MergeIcon color="primary" />
            Merge "{mergeSourceHost?.name}" into another host
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will transfer all social accounts, podcast assignments, and program assignments to the target host, then delete "{mergeSourceHost?.name}".
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search for target host..."
            value={mergeSearchInput}
            onChange={(e) => setMergeSearchInput(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            {mergeHostsLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : mergeHosts.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No hosts found
                </Typography>
              </Box>
            ) : (
              mergeHosts.slice(0, 20).map((host) => (
                <Box
                  key={host._id}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    backgroundColor: mergeTargetHostId === host._id ? 'primary.light' : 'transparent',
                    '&:hover': { backgroundColor: mergeTargetHostId === host._id ? 'primary.light' : 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => setMergeTargetHostId(host._id)}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {host.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {host.assignedPodcasts?.length || 0} podcasts, {host.assignedPrograms?.length || 0} programs
                    </Typography>
                  </Box>
                  {mergeTargetHostId === host._id && (
                    <Chip size="small" label="Selected" color="primary" />
                  )}
                </Box>
              ))
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMergeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleMerge}
            disabled={!mergeTargetHostId || mergeGuestsMutation.isPending}
          >
            {mergeGuestsMutation.isPending ? 'Merging...' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Host Dialog */}
      <Dialog open={createHostDialogOpen} onClose={() => setCreateHostDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Host</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Host Name"
            value={newHostName}
            onChange={(e) => setNewHostName(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="e.g., Juan Pérez"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newHostName.trim()) {
                createHostMutation.mutate(newHostName.trim());
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            After creating, you can assign podcasts, programs, and social accounts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateHostDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createHostMutation.mutate(newHostName.trim())}
            disabled={!newHostName.trim() || createHostMutation.isPending}
          >
            {createHostMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================
// Main Component
// ============================================
const SocialAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
            📱 Social Media Accounts
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="social admin tabs"
          >
            <Tab label="Overview" />
            <Tab label="Accounts" />
            <Tab label="Hosts" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <OverviewTab />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <AccountsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <HostsTab />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default SocialAdmin;
